import { Search, Sun, Moon, Menu } from "lucide-react";
import { useTheme } from "@/hooks/useTheme";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/useAuth";
import { useAdminProfile, initialsOf, firstNameOf } from "@/hooks/useAdminProfile";
import { useNavigate, useLocation } from "react-router-dom";
import { NotificationsDropdown } from "./NotificationsDropdown";

// Longest-prefix-first route → breadcrumb label map. Mirrors the sidebar IA
// (see AdminSidebar.tsx) plus the sub-pages that don't have their own nav
// entry anymore (still reachable via CommandPalette / direct link).
const ROUTE_LABELS: { prefix: string; label: string }[] = [
  { prefix: "/admin/leads-frios", label: "Leads frios" },
  { prefix: "/admin/leads", label: "Leads & propostas" },
  { prefix: "/admin/pipeline", label: "Leads & propostas" },
  { prefix: "/admin/sales", label: "Relatório de vendas" },
  { prefix: "/admin/projetos", label: "Projetos" },
  { prefix: "/admin/linear", label: "Projetos" },
  { prefix: "/admin/finance", label: "Financeiro" },
  { prefix: "/admin/conteudo", label: "Conteúdo" },
  { prefix: "/admin/blog", label: "Conteúdo" },
  { prefix: "/admin/projects", label: "Conteúdo" },
  { prefix: "/admin/testimonials", label: "Conteúdo" },
  { prefix: "/admin/links", label: "Conteúdo" },
  { prefix: "/admin/categories", label: "Conteúdo" },
  { prefix: "/admin/settings", label: "Configurações" },
];

function breadcrumbFor(pathname: string): string {
  if (pathname === "/admin") return "Comando";
  const match = ROUTE_LABELS.find((r) => pathname.startsWith(r.prefix));
  return match?.label ?? "Comando";
}

interface AdminHeaderProps {
  /** Below the admin shell's compact breakpoint (~1000px) — see Admin.tsx. */
  isMobile?: boolean;
  onOpenMobileNav?: () => void;
}

const iconBtn =
  "h-9 w-9 rounded-[9px] border-border bg-card text-muted-foreground hover:text-foreground";

export function AdminHeader({ isMobile = false, onOpenMobileNav }: AdminHeaderProps) {
  const { user, signOut } = useAuth();
  const { data: profile } = useAdminProfile();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const handleSignOut = async () => {
    await signOut();
    navigate("/admin/login");
  };

  const openCommandPalette = () =>
    document.dispatchEvent(
      new KeyboardEvent("keydown", {
        key: "k",
        ctrlKey: true,
        metaKey: true,
        bubbles: true,
        cancelable: true,
      }),
    );

  const displayName = profile?.name || user?.email || "Eliveuton Souza";

  return (
    <header className="sticky top-0 z-[5] h-16 shrink-0 border-b border-border bg-card">
      <div className="flex h-full items-center gap-4 px-4 md:px-6">
        {isMobile && (
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0"
            onClick={onOpenMobileNav}
            aria-label="Abrir menu"
          >
            <Menu className="h-5 w-5" />
          </Button>
        )}

        {/* Breadcrumb */}
        <div className="flex min-w-0 items-center gap-[9px]">
          <span className="text-[12.5px] text-muted-foreground">Painel</span>
          <span className="text-muted-foreground">/</span>
          <span className="truncate text-[13.5px] font-semibold">{breadcrumbFor(pathname)}</span>
        </div>

        <div className="flex-1" />

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={openCommandPalette}
            className="hidden h-9 w-[220px] items-center gap-2 rounded-[9px] border border-border bg-muted px-3 text-[13px] text-muted-foreground transition-colors hover:text-foreground sm:flex"
          >
            <Search className="h-[15px] w-[15px] shrink-0" strokeWidth={1.8} />
            <span className="flex-1 truncate text-left">Buscar ou executar…</span>
            <kbd className="inline-flex h-5 items-center rounded-[5px] border border-border bg-card px-[5px] font-mono text-[10.5px]">
              ⌘K
            </kbd>
          </button>

          <Button
            variant="outline"
            size="icon"
            className={iconBtn}
            onClick={toggleTheme}
            aria-label="Alternar tema"
          >
            {theme === "light" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
          </Button>

          <NotificationsDropdown />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-[9px] rounded-full border border-border bg-card py-1 pl-1 pr-1 transition-colors hover:bg-muted sm:pr-[10px]"
                aria-label="Menu do usuário"
              >
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-[11.5px] font-bold text-primary-foreground">
                  {initialsOf(profile?.name)}
                </span>
                <span className="hidden text-[12.5px] font-semibold sm:inline">
                  {firstNameOf(profile?.name)}
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[250px] rounded-[14px] p-1.5">
              <DropdownMenuLabel className="px-3 pb-2.5 pt-3">
                <div className="truncate text-[13px] font-bold">{displayName}</div>
                <div className="truncate text-[11.5px] font-normal text-muted-foreground">
                  {user?.email}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="rounded-[9px] px-3 py-[9px] text-[13px]"
                onClick={() => navigate("/admin/settings")}
              >
                Meu perfil
              </DropdownMenuItem>
              <DropdownMenuItem
                className="rounded-[9px] px-3 py-[9px] text-[13px]"
                onClick={() => navigate("/admin/settings?tab=general")}
              >
                Preferências
              </DropdownMenuItem>
              <DropdownMenuItem
                className="rounded-[9px] px-3 py-[9px] text-[13px]"
                onClick={() => navigate("/")}
              >
                Voltar ao site
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleSignOut}
                className="rounded-[9px] px-3 py-[9px] text-[13px] font-semibold text-bad focus:text-bad"
              >
                Sair
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
